// ============================================================
// ANSICHTSSACHE-AGENT – Eingabefeld
// ============================================================
// Die Beschriftung stand vorher in Versalien (`text-overline`).
// Auf einem Formular mit zwoelf Feldern schreit das — Versalien
// sind eine Auszeichnung fuer einzelne Zeilen, nicht fuer jede.
// Jetzt: normale Schriftgroesse, mittlere Staerke.
//
// Ein Feld mit `type="tel"` richtet seine Nummer selbst nach
// DIN 5008 — beim Einfuegen und beim Verlassen
// ([[components/ui/phone-field]]).
// ============================================================

"use client";

import { forwardRef } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { FieldLabel } from "./field-label";
import { DateInput } from "./date-input";
import { noAutocorrect } from "./no-autocorrect";
import { handlePhonePaste, loadPhoneFormatter, tidyPhoneField } from "./phone-field";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  /**
   * Fuer die Huelle um Beschriftung, Feld und Fehlertext. Ohne
   * sie laesst sich das Feld nicht in eine Zeile stellen, in der
   * daneben noch etwas steht — ein „EUR“ etwa.
   */
  wrapperClassName?: string;
  /**
   * Nur bei `type="tel"`: das Land, in dem eine Nummer OHNE
   * Laenderkennzahl gelesen wird — beim Kunden in Salzburg ist
   * „0662 123456“ Salzburg. Vorgabe ist Deutschland.
   */
  phoneCountry?: string | null;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, wrapperClassName, type, phoneCountry, ...props }, ref) => {
    const inputId = id || props.name;
    // Ein Telefonfeld richtet die Nummer selbst; was der Aufrufer
    // an Focus, Einfuegen und Verlassen haengt, laeuft danach
    // bzw. davor wie gewohnt.
    const phoneHandlers =
      type === "tel"
        ? {
            onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
              void loadPhoneFormatter();
              props.onFocus?.(event);
            },
            onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
              props.onPaste?.(event);
              handlePhonePaste(event, phoneCountry);
            },
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              tidyPhoneField(event.currentTarget, phoneCountry);
              props.onBlur?.(event);
            },
          }
        : null;
    const feld = {
      id: inputId,
      className: cn(
        "bg-bg-primary text-body-sm text-text-primary flex h-10 w-full rounded-lg border px-3",
        "placeholder:text-text-tertiary",
        // Nur `box-shadow` und `border-color` bewegen sich —
        // `transition-all` liess auch die Breite mitfahren,
        // wenn ein Feld in einem Raster neu umbrach.
        "duration-fast ease-standard transition-[box-shadow,border-color]",
        "hover:border-border-strong",
        "focus:border-accent/40 focus:ring-2 focus:outline-none",
        "disabled:hover:border-border disabled:cursor-not-allowed disabled:opacity-40",
        error
          ? "border-danger text-danger focus:ring-danger/30"
          : "border-border focus:ring-accent/30",
        className,
      ),
      "aria-invalid": error ? ("true" as const) : undefined,
      "aria-describedby": error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined,
      // Ein Suchfeld korrigiert nicht ([[no-autocorrect]]).
      ...(type === "search" ? noAutocorrect : null),
      ...props,
      ...phoneHandlers,
    };

    return (
      <div className={cn("space-y-1.5", wrapperClassName)}>
        {label && <FieldLabel htmlFor={inputId}>{label}</FieldLabel>}
        {/* Ein Datum geht durch sein eigenes Bauteil: Safari zeigte im
            leeren Feld sonst das heutige Datum ([[date-input]]). */}
        {type === "date" ? (
          <DateInput ref={ref} {...feld} />
        ) : (
          <input ref={ref} type={type} {...feld} />
        )}
        {error && (
          <p
            id={`${inputId}-error`}
            className="text-caption text-danger flex items-start gap-1.5"
            role="alert"
          >
            <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-caption text-text-tertiary">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
